/**
 * LiftUtilizationReport Component
 *
 * Displays lift utilization data with a bar chart and table.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Stack,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { chartTooltipProps } from '@/lib/chartTheme';
import { useLiftUtilization } from '@/hooks/useLifts';

export function LiftUtilizationReport() {
  const { t } = useTranslation('reports');
  const theme = useTheme();

  // Default to last 7 days
  const today = new Date();
  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

  const [startDate, setStartDate] = useState(weekAgo.toISOString().split('T')[0]!);
  const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]!);

  const { data: report, isLoading } = useLiftUtilization(
    new Date(startDate).toISOString(),
    new Date(endDate + 'T23:59:59').toISOString()
  );

  const chartData = report?.data.map((lift) => ({
    name: lift.identifier,
    utilization: lift.utilizationPercent,
    jobs: lift.jobCount,
  })) ?? [];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('liftUtilization.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {t('liftUtilization.subtitle')}
      </Typography>

      <Stack direction="row" spacing={2} mb={3}>
        <TextField
          type="date"
          label={t('liftUtilization.startDate')}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          size="small"
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          type="date"
          label={t('liftUtilization.endDate')}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          size="small"
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Stack>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : !report || report.data.length === 0 ? (
        <Alert severity="info">{t('liftUtilization.noData')}</Alert>
      ) : (
        <>
          {/* Overall utilization chip */}
          <Stack direction="row" spacing={2} mb={3} alignItems="center">
            <Typography variant="subtitle2">{t('liftUtilization.overall')}:</Typography>
            <Chip
              label={`${report.overall.utilizationPercent}%`}
              color={report.overall.utilizationPercent > 70 ? 'success' : report.overall.utilizationPercent > 40 ? 'warning' : 'default'}
            />
          </Stack>

          {/* Bar chart */}
          <Box sx={{ width: '100%', height: 300, mb: 3 }}>
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis domain={[0, 100]} unit="%" />
                <Tooltip
                  {...chartTooltipProps(theme)}
                  formatter={(value: number) => [`${value}%`, t('liftUtilization.utilization')]}
                />
                <Bar dataKey="utilization" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>

          {/* Table */}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('liftUtilization.liftName')}</TableCell>
                  <TableCell align="right">{t('liftUtilization.availableHours')}</TableCell>
                  <TableCell align="right">{t('liftUtilization.usedHours')}</TableCell>
                  <TableCell align="right">{t('liftUtilization.utilization')}</TableCell>
                  <TableCell align="right">{t('liftUtilization.jobCount')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.data.map((lift) => (
                  <TableRow key={lift.id}>
                    <TableCell>{lift.name} ({lift.identifier})</TableCell>
                    <TableCell align="right">
                      {(lift.totalAvailableMinutes / 60).toFixed(1)}
                    </TableCell>
                    <TableCell align="right">
                      {(lift.totalUsedMinutes / 60).toFixed(1)}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        size="small"
                        label={`${lift.utilizationPercent}%`}
                        color={lift.utilizationPercent > 70 ? 'success' : lift.utilizationPercent > 40 ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">{lift.jobCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
